/**
 * Painel de login (e-mail + senha) do design.
 *
 * Só apresentação e validação local: quem fala com o backend é o container
 * (pages/Auth/Login.jsx), que recebe `onEntrar` e devolve `estado`/`erroServidor`.
 *
 * Fora do design por decisão do dono (23-24/10): "Manter conectado" não
 * existe e "Esqueci minha senha" não tem autoatendimento — mostra a
 * orientação na própria tela.
 */
import React, { useState } from "react";

import { MensagemAviso, MensagemErro } from "./MensagemCampo";
import { capsLockLigado, emailValido } from "./logica";
import { IconeCadeado, IconeEmail, IconeEntrar, IconeOlho, IconeOlhoRiscado, IconePredio } from "./icones";

export const ESTADO_PARADO = "idle";
export const ESTADO_LOGANDO = "logando";
export const ESTADO_LENTO = "lento";
export const ESTADO_MUITO_LENTO = "muito_lento";

const ERRO_EMAIL = "email";
const ERRO_SENHA = "senha";

// Servidor dormindo (cold start): sem estas mensagens a pessoa vê
// "Verificando…" por 30 s sem entender o porquê.
const TEXTO_LENTO = {
  [ESTADO_LENTO]: "Conectando ao servidor…",
  [ESTADO_MUITO_LENTO]: "Servidor inativo está acordando, aguarde…",
};

function CampoEmail({ valor, aoMudar, invalido }) {
  return (
    <div className="f">
      <label className="f-l" htmlFor="erp-email">E-mail</label>
      <div className={`f-box${invalido ? " bad" : ""}`}>
        <IconeEmail />
        <input
          id="erp-email"
          type="text"
          inputMode="email"
          autoComplete="username"
          spellCheck={false}
          autoCapitalize="none"
          placeholder="nome@suamarcenaria.com.br"
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          aria-invalid={invalido || undefined}
          aria-describedby={invalido ? "erp-email-erro" : undefined}
        />
      </div>
      {invalido && <MensagemErro id="erp-email-erro">Informe um e-mail válido.</MensagemErro>}
    </div>
  );
}

function CampoSenha({ valor, aoMudar, invalido }) {
  const [visivel, setVisivel] = useState(false);
  const [caps, setCaps] = useState(false);
  const conferirCaps = (evento) => setCaps(capsLockLigado(evento));
  const descricao = [invalido && "erp-senha-erro", caps && "erp-senha-caps"].filter(Boolean).join(" ");
  return (
    <div className="f">
      <label className="f-l" htmlFor="erp-senha">Senha</label>
      <div className={`f-box${invalido ? " bad" : ""}`}>
        <IconeCadeado />
        <input
          id="erp-senha"
          type={visivel ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Sua senha"
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          onKeyDown={conferirCaps}
          onKeyUp={conferirCaps}
          onBlur={() => setCaps(false)}
          aria-invalid={invalido || undefined}
          aria-describedby={descricao || undefined}
        />
        <button
          className="eye"
          type="button"
          aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
          aria-pressed={visivel}
          onClick={() => setVisivel((v) => !v)}
        >
          {visivel ? <IconeOlhoRiscado /> : <IconeOlho />}
        </button>
      </div>
      {invalido && <MensagemErro id="erp-senha-erro">Digite sua senha.</MensagemErro>}
      {caps && (
        <MensagemAviso id="erp-senha-caps" icone="caps">
          Caps Lock está ativado.
        </MensagemAviso>
      )}
    </div>
  );
}

function EsqueciSenha({ quemAjuda }) {
  const [aberta, setAberta] = useState(false);
  return (
    <>
      <div className="row so-link">
        <button
          className="lnk"
          type="button"
          aria-expanded={aberta}
          aria-controls="erp-esqueci"
          onClick={() => setAberta((v) => !v)}
        >
          Esqueci minha senha
        </button>
      </div>
      <div id="erp-esqueci" role="status">
        {aberta && (
          <p className="nota">
            Peça {quemAjuda} para redefinir sua senha. Você recebe uma senha provisória e troca no primeiro acesso.
          </p>
        )}
      </div>
    </>
  );
}

export default function PainelLogin({ marca, emailInicial = "", estado = ESTADO_PARADO, erroServidor = "", onEntrar }) {
  const [email, setEmail] = useState(emailInicial);
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const ocupado = estado !== ESTADO_PARADO;

  function enviar(evento) {
    evento.preventDefault();
    if (ocupado) return;
    if (!emailValido(email)) {
      setErro(ERRO_EMAIL);
      return;
    }
    if (!senha) {
      setErro(ERRO_SENHA);
      return;
    }
    setErro("");
    onEntrar({ email: email.trim(), password: senha });
  }

  return (
    <div className="pane">
      <span className="tenant">
        <IconePredio />
        {marca.chip}
      </span>
      <h1 className="a-h">{marca.titulo}</h1>
      <p className="a-p">{marca.subtitulo}</p>

      <form onSubmit={enviar} noValidate>
        <CampoEmail
          valor={email}
          invalido={erro === ERRO_EMAIL}
          aoMudar={(v) => {
            setEmail(v);
            setErro("");
          }}
        />
        <CampoSenha
          valor={senha}
          invalido={erro === ERRO_SENHA}
          aoMudar={(v) => {
            setSenha(v);
            setErro("");
          }}
        />
        <EsqueciSenha quemAjuda={marca.quemAjuda} />
        {erroServidor && <MensagemErro className="srv">{erroServidor}</MensagemErro>}
        <button className={`e-btn${ocupado ? " busy" : ""}`} type="submit" aria-busy={ocupado || undefined}>
          {ocupado ? (
            <>
              <span className="spin" aria-hidden="true" />
              Verificando…
            </>
          ) : (
            <>
              <IconeEntrar />
              Entrar
            </>
          )}
        </button>
        <div role="status">{TEXTO_LENTO[estado] && <p className="hint lento">{TEXTO_LENTO[estado]}</p>}</div>
      </form>

      <p className="alt">
        Primeiro acesso ou usuário bloqueado? Peça {marca.quemAjuda} para liberar o seu acesso.
      </p>
    </div>
  );
}
